import { useState } from 'react'
import { MessageSquare, ThumbsUp, AlertCircle, Search, Filter, CheckCircle, Clock, Trash2, Reply } from 'lucide-react'

export default function SocialMediaCommentMonitorPage() {
  const [comments, setComments] = useState([
    { id: 1, author: 'John Tech', platform: 'Facebook', content: 'Amazing product! Just implemented it and already seeing great results.', sentiment: 'Positive', timestamp: '2 hours ago', likes: 23, status: 'Active', post: 'New security update...' },
    { id: 2, author: 'Sarah Security', platform: 'Twitter', content: 'How does this compare to competitors?', sentiment: 'Neutral', timestamp: '3 hours ago', likes: 5, status: 'Unanswered', post: 'Launch announcement...' },
    { id: 3, author: 'Mike Secure', platform: 'Instagram', content: 'Love the interface, but having issues with integration. Help?', sentiment: 'Mixed', timestamp: '4 hours ago', likes: 12, status: 'Unanswered', post: 'Customer spotlight...' },
    { id: 4, author: 'Anna Dev', platform: 'LinkedIn', content: 'Finally a security solution that understands enterprise needs!', sentiment: 'Positive', timestamp: '5 hours ago', likes: 45, status: 'Active', post: 'Q&A session...' },
    { id: 5, author: 'Critic Carl', platform: 'Twitter', content: 'Not worth the hype. Overpriced for what it does.', sentiment: 'Negative', timestamp: '6 hours ago', likes: 3, status: 'Flagged', post: 'Launch announcement...' },
    { id: 6, author: 'Happy Customer', platform: 'Facebook', content: 'Best investment we made for our company!', sentiment: 'Positive', timestamp: '7 hours ago', likes: 67, status: 'Active', post: 'New security update...' },
  ])

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSentiment, setSelectedSentiment] = useState('all')
  const [selectedPlatform, setSelectedPlatform] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')

  const filteredComments = comments.filter(comment => {
    const matchesSearch =
      comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.author.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesSentiment = selectedSentiment === 'all' || comment.sentiment === selectedSentiment
    const matchesPlatform = selectedPlatform === 'all' || comment.platform === selectedPlatform
    const matchesStatus = selectedStatus === 'all' || comment.status === selectedStatus

    return matchesSearch && matchesSentiment && matchesPlatform && matchesStatus
  })

  const sentimentCounts = {
    positive: comments.filter(c => c.sentiment === 'Positive').length,
    negative: comments.filter(c => c.sentiment === 'Negative').length,
    neutral: comments.filter(c => c.sentiment === 'Neutral').length,
    mixed: comments.filter(c => c.sentiment === 'Mixed').length,
  }

  const statusCounts = {
    active: comments.filter(c => c.status === 'Active').length,
    unanswered: comments.filter(c => c.status === 'Unanswered').length,
    flagged: comments.filter(c => c.status === 'Flagged').length,
  }

  const getSentimentColor = (sentiment) => {
    switch(sentiment) {
      case 'Positive': return 'bg-green-500/30 text-green-300'
      case 'Negative': return 'bg-red-500/30 text-red-300'
      case 'Neutral': return 'bg-blue-500/30 text-blue-300'
      case 'Mixed': return 'bg-yellow-500/30 text-yellow-300'
      default: return 'bg-slate-500/30 text-slate-300'
    }
  }

  const getSentimentIcon = (sentiment) => {
    switch(sentiment) {
      case 'Positive': return '😊'
      case 'Negative': return '😞'
      case 'Neutral': return '😐'
      case 'Mixed': return '🤔'
      default: return '❓'
    }
  }

  const markAsAnswered = (id) => {
    setComments(comments.map(c => c.id === id ? { ...c, status: 'Active' } : c))
  }

  const deleteComment = (id) => {
    setComments(comments.filter(c => c.id !== id))
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Social Media Comment Monitor</h1>
        <p className="text-blue-200">Track, filter, and respond to comments across all your social media platforms</p>
      </div>

      {/* Sentiment Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-green-300/70 text-sm">Positive</p>
            <span className="text-2xl">😊</span>
          </div>
          <p className="text-3xl font-bold text-green-400">{sentimentCounts.positive}</p>
          <p className="text-xs text-green-300 mt-2">{((sentimentCounts.positive / comments.length) * 100).toFixed(0)}% of comments</p>
        </div>

        <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-lg border border-red-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-red-300/70 text-sm">Negative</p>
            <span className="text-2xl">😞</span>
          </div>
          <p className="text-3xl font-bold text-red-400">{sentimentCounts.negative}</p>
          <p className="text-xs text-red-300 mt-2">{((sentimentCounts.negative / comments.length) * 100).toFixed(0)}% of comments</p>
        </div>

        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-blue-300/70 text-sm">Neutral</p>
            <span className="text-2xl">😐</span>
          </div>
          <p className="text-3xl font-bold text-blue-400">{sentimentCounts.neutral}</p>
          <p className="text-xs text-blue-300 mt-2">{((sentimentCounts.neutral / comments.length) * 100).toFixed(0)}% of comments</p>
        </div>

        <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 rounded-lg border border-yellow-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-yellow-300/70 text-sm">Mixed</p>
            <span className="text-2xl">🤔</span>
          </div>
          <p className="text-3xl font-bold text-yellow-400">{sentimentCounts.mixed}</p>
          <p className="text-xs text-yellow-300 mt-2">{((sentimentCounts.mixed / comments.length) * 100).toFixed(0)}% of comments</p>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-300/70 text-sm mb-1">Active</p>
              <p className="text-3xl font-bold text-blue-400">{statusCounts.active}</p>
            </div>
            <CheckCircle size={32} className="text-blue-400" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 rounded-lg border border-yellow-500/30 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-300/70 text-sm mb-1">Unanswered</p>
              <p className="text-3xl font-bold text-yellow-400">{statusCounts.unanswered}</p>
            </div>
            <Clock size={32} className="text-yellow-400" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-lg border border-red-500/30 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-300/70 text-sm mb-1">Flagged</p>
              <p className="text-3xl font-bold text-red-400">{statusCounts.flagged}</p>
            </div>
            <AlertCircle size={32} className="text-red-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <Filter size={24} />
          <span>Filter Comments</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-blue-300 font-semibold text-sm mb-2 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 text-blue-300" size={20} />
              <input
                type="text"
                placeholder="Search comments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
              />
            </div>
          </div>

          <div>
            <label className="text-blue-300 font-semibold text-sm mb-2 block">Sentiment</label>
            <select
              value={selectedSentiment}
              onChange={(e) => setSelectedSentiment(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-500/60"
            >
              <option value="all">All Sentiments</option>
              <option value="Positive">Positive</option>
              <option value="Negative">Negative</option>
              <option value="Neutral">Neutral</option>
              <option value="Mixed">Mixed</option>
            </select>
          </div>

          <div>
            <label className="text-blue-300 font-semibold text-sm mb-2 block">Platform</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-500/60"
            >
              <option value="all">All Platforms</option>
              <option value="Facebook">Facebook</option>
              <option value="Twitter">Twitter</option>
              <option value="Instagram">Instagram</option>
              <option value="LinkedIn">LinkedIn</option>
            </select>
          </div>

          <div>
            <label className="text-blue-300 font-semibold text-sm mb-2 block">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-500/60"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Unanswered">Unanswered</option>
              <option value="Flagged">Flagged</option>
            </select>
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <MessageSquare size={24} />
          <span>Comments ({filteredComments.length})</span>
        </h2>

        <div className="space-y-4">
          {filteredComments.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare size={48} className="text-blue-300 mx-auto mb-4 opacity-50" />
              <p className="text-blue-200">No comments found matching your filters</p>
            </div>
          ) : (
            filteredComments.map((comment) => (
              <div key={comment.id} className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20 hover:border-blue-500/40 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <p className="font-bold text-white">{comment.author}</p>
                      <span className="text-xs bg-blue-500/30 text-blue-300 px-2 py-1 rounded">{comment.platform}</span>
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${getSentimentColor(comment.sentiment)}`}>
                        {getSentimentIcon(comment.sentiment)} {comment.sentiment}
                      </span>
                    </div>
                    <p className="text-blue-100 mb-2">{comment.content}</p>
                    <div className="flex items-center space-x-3 text-xs text-blue-300">
                      <span>On: "{comment.post}"</span>
                      <span>•</span>
                      <span>{comment.timestamp}</span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="flex items-center space-x-1 text-red-400 mb-2">
                      <ThumbsUp size={16} />
                      <span className="font-semibold">{comment.likes}</span>
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full block ${
                      comment.status === 'Active' ? 'bg-green-500/30 text-green-300' :
                      comment.status === 'Unanswered' ? 'bg-yellow-500/30 text-yellow-300' :
                      'bg-red-500/30 text-red-300'
                    }`}>
                      {comment.status}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2 pt-3 border-t border-blue-500/20">
                  {comment.status === 'Unanswered' && (
                    <button
                      onClick={() => markAsAnswered(comment.id)}
                      className="px-3 py-1 bg-blue-600/30 text-blue-300 hover:bg-blue-600/50 rounded text-xs font-semibold flex items-center space-x-1 transition-all"
                    >
                      <Reply size={14} />
                      <span>Mark Answered</span>
                    </button>
                  )}
                  <button
                    onClick={() => deleteComment(comment.id)}
                    className="px-3 py-1 bg-red-600/30 text-red-300 hover:bg-red-600/50 rounded text-xs font-semibold flex items-center space-x-1 transition-all ml-auto"
                  >
                    <Trash2 size={14} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
        <h3 className="text-lg font-bold text-white mb-3 flex items-center space-x-2">
          <AlertCircle size={20} />
          <span>Comment Management Tips</span>
        </h3>
        <ul className="space-y-2 text-sm text-blue-200">
          <li>✓ Respond to negative comments within 24 hours to show you care</li>
          <li>✓ Use filters to prioritize unanswered questions</li>
          <li>✓ Monitor sentiment to track customer satisfaction trends</li>
          <li>✓ Flag comments that require special attention or escalation</li>
          <li>✓ Engage with positive comments to build community</li>
        </ul>
      </div>
    </div>
  )
}
